import * as React from 'react';
import * as pdfjs from 'pdfjs-dist/legacy/build/pdf'; // https://github.com/mozilla/pdf.js/issues/13200
import { DocumentInitParameters } from 'pdfjs-dist/types/src/display/api.d';
import { FileType, Logging } from '@vcd-ecm-ui/Common';
import DocumentViewerProps from './DocumentViewerProps.interface';
import displayImageOnCanvas from '../../helpers/ZoomPanning';
import '../document-viewer.scss';

// Get the PDF Version
const pdfVersion = pdfjs.version;

// https://mozilla.github.io/pdf.js/getting_started/#including-via-a-cdn

// Set the Font Directory
const standardFontDataUrlBackup1 = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfVersion}/standard_fonts/`;
const standardFontDataUrlBackup2 = `https://unpkg.com/pdfjs-dist@${pdfVersion}/standard_fonts/`;
const standardFontDataUrlBackup3 = 'https://mozilla.github.io/pdf.js/web/standard_fonts/';

const workerUrlBackup1 = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfVersion}/legacy/build/pdf.worker.min.js`;
const workerUrlBackup2 = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfVersion}/pdf.worker.min.js`;
const workerUrlBackup3 = `https://unpkg.com/pdfjs-dist@${pdfVersion}/legacy/build/pdf.worker.min.js`;

const DocumentViewer = ({
  idPrefix,
  canRotate,
  canDelete = false,
  imageDocument,
  setIsPortrait,
  onDelete = null,
  additionalControlsStyle = '',
}: DocumentViewerProps): JSX.Element => {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [numberPages, setNumberPages] = React.useState(null);
  const [pageNumber, setPageNumber] = React.useState(1);
  const [loadedDocument, setLoadedDocument] = React.useState(null);
  const [controlHorizontal, setControlHorizontal] = React.useState(false);
  const [rotateAngle, setRotateAngle] = React.useState(0);
  const [documentError, setDocumentError] = React.useState(null);

  const pdfjsWorkerUrl = workerUrlBackup1;
  const pdfjsStandardFontUrl = standardFontDataUrlBackup1;

  // Configure the PDF Worker
  pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;

  Logging.Debug('PDFJS', pdfjsWorkerUrl, pdfjsStandardFontUrl);

  // Assign the Prefix before the Id.
  // The Ids of Buttons used in Document Viewer needs to be unique as their are multiple Document Viewer instances
  // Event Handlers are assigned to the buttons
  const getId = (id: string): string => (`${idPrefix}-${id}`);

  const isDisabled = (id: string): boolean => {
    const button: HTMLButtonElement = document.getElementById(getId(id)) as HTMLButtonElement;

    return button ? button.disabled : true;
  };

  // Previous Page Button pressed
  const onPreviousPage = (): void => {
    if (pageNumber > 1) {
      // Prevent Paging if the Button is Disabled.  The button may not have been rendered in DOM yet
      if (!isDisabled('previousPageButton')) {
        Logging.Debug('onPreviousPage', pageNumber - 1);
        setPageNumber(pageNumber - 1);
      }
    }
  };

  // Next Page Button Pressed
  const onNextPage = (): void => {
    if (pageNumber < numberPages) {
      // Prevent Paging if the Button is Disabled. The button may not have been rendered in DOM yet
      if (!isDisabled('nextPageButton')) {
        Logging.Debug('onNextPage', pageNumber + 1);
        setPageNumber(pageNumber + 1);
      }
    }
  };

  // Rotate Button Pressed
  const onRotate = (): void => {
    // Prevent Rotation if the Button is Disabled. The button may not have been rendered in DOM yet
    if (!isDisabled('rotateButton')) {
      let angle = rotateAngle;

      // Increment the Rotation in 90 degree steps clockwise
      angle += 90;

      // Limit the Rotation
      if (angle >= 360) {
        angle = 0;
      }

      Logging.Debug('onRotate', angle);

      // Update the Rotation Angle
      setRotateAngle(angle);
    }
  };

  // Document Controls orientation changed
  const onControlOrientation = (): void => {
    // setControlHorizontal(!controlHorizontal);
  };

  // Mouse Moved inside browser
  const onMouseMove = (event: MouseEvent, documentContainer: HTMLDivElement, controls: HTMLDivElement): void => {
    const controlsAlwaysVisible = false;

    // Get the Bounding rect of the Document Container
    const rect: DOMRect = documentContainer.getBoundingClientRect();

    // Check if the Mouse is over the Document.  If so make the Controls visible, otherwise hide them
    if ((event.clientX >= rect.left && event.clientX <= rect.right
      && event.clientY >= rect.top && event.clientY <= rect.bottom)
      || controlsAlwaysVisible) {
      controls.setAttribute('style', 'visibility:visible');
    } else {
      controls.setAttribute('style', 'visibility:hidden');
    }
  };

  // Enable / Disable Button
  const enableDisableButton = (id: string, disabled: boolean): void => {
    const button: HTMLButtonElement = document.getElementById(getId(id)) as HTMLButtonElement;

    if (button) {
      button.disabled = disabled;
    }
  };

  const disableControls = (): void => {
    // Prevent the following Error:
    // "Cannot use the same canvas during multiple render() operations.
    // Use different canvas or ensure previous operations were cancelled or completed."
    Logging.Debug('disableControls');

    enableDisableButton('previousPageButton', true);
    enableDisableButton('nextPageButton', true);
    enableDisableButton('rotateButton', true);
  };

  const enableControls = (): void => {
    // Prevent the following Error:
    // "Cannot use the same canvas during multiple render() operations.
    // Use different canvas or ensure previous operations were cancelled or completed."
    Logging.Debug('enableControls');

    enableDisableButton('previousPageButton', false);
    enableDisableButton('nextPageButton', false);
    enableDisableButton('rotateButton', false);
  };

  // Display an Image on the Canvas
  const displayImage = (canvas: HTMLCanvasElement, image: HTMLImageElement, initialDraw: boolean): void => {
    // For non PDF images, inform any Containers that want to adjust their Orientation Mode (Portraint vs Landscape)
    if (setIsPortrait && (imageDocument.contentType !== FileType.Pdf)) {
      const isPortrait: boolean = image.width < image.height;

      setIsPortrait(isPortrait);
    }

    // Get the Document Container
    const documentContainer: HTMLDivElement = document.getElementById(getId('sbsDocumentViewer')) as HTMLDivElement;
    const controlsContainer: HTMLDivElement = document.getElementById(getId('CanvasControls')) as HTMLDivElement;

    // Track the Mousement of the Mouse over the Body to Show/Hide the Controls
    if (documentContainer && controlsContainer) {
      document.body.addEventListener(
        'mousemove',
        (event: MouseEvent) => onMouseMove(event, documentContainer, controlsContainer),
        false,
      );
    }

    const zoomInElements: string[] = [getId('zoomInButton')];
    const zoomOutElements: string[] = [getId('zoomOutButton')];
    const zoomPercentageElements: string[] = [getId('zoomPercentage')];
    const restoreElements: string[] = [getId('restoreButton')];

    // Display the Image on the Canvas
    displayImageOnCanvas(
      canvas,
      image,
      initialDraw,
      zoomInElements,
      zoomOutElements,
      zoomPercentageElements,
      restoreElements,
    );

    // Enable the Controls. Image / Page is done rendering
    enableControls();
  };

  // Render a PDF Document to a Canvas
  const pdfToCancas = (): void => {
    try {
      // Disable the Controls so the Document has time to render.  May take couple of seconds
      disableControls();

      // Load the PDF Document if not already loaded
      if (loadedDocument === null) {
        // Load the Image from a Local File object
        if (imageDocument.file) {
          const reader = new FileReader();

          // Reader Abort
          reader.onabort = (): void => {
            Logging.Info('Abort');
            setDocumentError('Error occurred while loading document.');
          };
          // Reader Error
          reader.onerror = (): void => {
            Logging.Info('Error');
            setDocumentError('Error occurred while loading document.');
          };

          // Reader Success
          reader.onload = (): void => {
            const docInitParams: DocumentInitParameters = {
              data: reader.result as any, // eslint-disable-line @typescript-eslint/no-explicit-any
              standardFontDataUrl: pdfjsStandardFontUrl,
            };

            pdfjs.getDocument(docInitParams).promise.then((pdf: pdfjs.PDFDocumentProxy) => {
              Logging.Info('PDF Loaded for Source', pdfjs.version, pdf.numPages);

              // Set the Number of Pages
              setNumberPages(pdf.numPages);

              // Save the Loaded Document
              setLoadedDocument(pdf);

              // Done Loading
              setIsLoaded(true);
            }, (error: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
              Logging.Error('Error occurred while loading document:', error);
              setDocumentError('Error occurred while loading document.');
            });
          };

          reader.readAsArrayBuffer(imageDocument.file);
          // Load Image from a URL
        } else {
          const docInitParams: DocumentInitParameters = {
            url: imageDocument.location,
            standardFontDataUrl: pdfjsStandardFontUrl,
          };

          pdfjs.getDocument(docInitParams).promise.then((pdf: pdfjs.PDFDocumentProxy) => {
            Logging.Info('PDF Loaded from Url', pdfjs.version, pdf.numPages);

            // Set the Number of Pages
            setNumberPages(pdf.numPages);

            // Save the Loaded Document
            setLoadedDocument(pdf);

            // Done Loading
            setIsLoaded(true);
          }, (error: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
            Logging.Error('Error occurred while loading document:', error);
            setDocumentError('Error occurred while loading document.');
          });
        }
      } else {
        const pdf: pdfjs.PDFDocumentProxy = loadedDocument as pdfjs.PDFDocumentProxy;

        // Get the correct Page Number, 1 to n
        pdf.getPage(pageNumber).then((page: pdfjs.PDFPageProxy) => {
          Logging.Debug('PDF Get Page', pageNumber);

          const canvas: HTMLCanvasElement = document.getElementById(getId('Canvas')) as HTMLCanvasElement;

          if (canvas) {
            // Get the 2D Context
            const context: CanvasRenderingContext2D = canvas.getContext('2d') as CanvasRenderingContext2D;

            // This Scale is just to provide a better quality initial Image
            const viewport = page.getViewport({ scale: 3.0 });

            // Adjust the Cnavas to the Page Size
            canvas.width = viewport.width;
            canvas.height = viewport.height;

            // Render the Page
            page.render({ canvasContext: context, viewport }).promise.then(() => {
              // Get Image from Rendered Page
              const image = new Image();
              image.src = canvas.toDataURL(FileType.Png);

              // Display the Image on the Canvas
              displayImage(canvas, image, false);

              Logging.Debug('PDF Page Render Complete');
            }, (error: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
              Logging.Error('Error occurred while drawing page:', error);
              setDocumentError('Error occurred while drawing page.');
            });
          }
        }, (error: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
          Logging.Error('Error occurred with the page:', error);
          setDocumentError('Error occurred while loading page.');
        });
      }
    } catch (error) { // eslint-disable-line @typescript-eslint/no-explicit-any
      Logging.Error('Error occurred with the document:', error);
      setDocumentError('Error occurred with the document.');
    }
  };

  // Create a Rotated version of the supplied Image
  const rotateImage = (image: HTMLImageElement, angleDegress: number): HTMLImageElement => {
    const radians = (angleDegress * Math.PI) / 180;
    const sin: number = Math.sin(radians);
    const cos: number = Math.cos(radians);

    // Get the Image Width
    const imageWidth = image.width;
    const imageHeight = image.height;

    // Get the Full Width & Height after Rotation
    const fullWidth: number = Math.abs(sin) * imageHeight + Math.abs(cos) * imageWidth;
    const fullHeight: number = Math.abs(cos) * imageHeight + Math.abs(sin) * imageWidth;

    // Create a Temporary Canvas
    const temporaryCanvas: HTMLCanvasElement = document.createElement('canvas') as HTMLCanvasElement;

    if (temporaryCanvas) {
      // Set the Canvas Width & Height
      temporaryCanvas.setAttribute('width', fullWidth.toString());
      temporaryCanvas.setAttribute('height', fullHeight.toString());

      // Create a Temporary Context and draw the Rotated Image
      const temporaryContext: CanvasRenderingContext2D = temporaryCanvas.getContext('2d') as CanvasRenderingContext2D;

      // Draw the Rotated Image
      temporaryContext.translate(fullWidth / 2, fullHeight / 2);
      temporaryContext.rotate(radians);
      temporaryContext.drawImage(image, -imageWidth / 2, -imageHeight / 2, imageWidth, imageHeight);

      // Get the Rotated Image from the Canvas
      const rotatedImage = new Image();
      // Allowing cross-origin use of images and canvas
      // https://developer.mozilla.org/en-US/docs/Web/HTML/CORS_enabled_image
      rotatedImage.crossOrigin = 'Anonymous';
      rotatedImage.src = temporaryCanvas.toDataURL(FileType.Png);

      // Remove the Temporary Canvas
      temporaryCanvas.remove();

      // This Image needs to be reloaded.  Width and Height may still be zero
      return rotatedImage;
    }

    return null;
  };

  // Render an Image (Png, Jpg or Gif) to a Canvas
  const imageToCancas = (): void => {
    try {
      // Disable the Controls so the Image has time to render.  May take couple of seconds
      disableControls();

      // Document not yet Loaded
      if (loadedDocument === null) {
        // Create the Image
        const image = new Image();
        // Allowing cross-origin use of images and canvas
        // https://developer.mozilla.org/en-US/docs/Web/HTML/CORS_enabled_image
        image.crossOrigin = 'Anonymous';

        // Load from Local File or Remote Url
        image.src = imageDocument.file ? URL.createObjectURL(imageDocument.file) : imageDocument.location;

        // Load the Image
        image.onload = (): void => {
          Logging.Debug('Image Loaded');

          // Save the Loaded Document
          setLoadedDocument(image);

          // Done Loading
          setIsLoaded(true);
        };

        // Error Loading Image
        image.onerror = (error): void => { // eslint-disable-line @typescript-eslint/no-explicit-any
          Logging.Error('Error occurred while loading image.', error);
          setDocumentError('Error occurred while loading image.');
        };
      } else {
        // Get the Image
        const image = loadedDocument as HTMLImageElement;

        const canvas: HTMLCanvasElement = document.getElementById(getId('Canvas')) as HTMLCanvasElement;

        if (canvas) {
          if (rotateAngle === 0) {
            // Adjust the Canvas to the Image Size
            canvas.width = image.width;
            canvas.height = image.height;

            // Display the Image
            displayImage(canvas, image, true);
          } else {
            // Create a Rotated Image from the Original Image
            const rotatedImage: HTMLImageElement = rotateImage(image, rotateAngle);

            if (rotatedImage) {
              // Reloaded Image again, otherwise Width & Height will be 0
              rotatedImage.onload = (): void => {
                // Adjust the canvas the Image Size
                canvas.width = rotatedImage.width;
                canvas.height = rotatedImage.height;

                // Display the Image
                displayImage(canvas, rotatedImage, true);
              };
              // Error Loading Image
              rotatedImage.onerror = (error): void => { // eslint-disable-line @typescript-eslint/no-explicit-any
                Logging.Error('Error occurred while loading image.', error);
                setDocumentError('Error occurred while loading image.');
              };
            }
          }
        }
      }
    } catch (error) {
      Logging.Error('Error occurred while loading image:', error);
      setDocumentError('Error occurred with the image.');
    }
  };

  // Render the Errors.  May be Single or Array or Errors
  const renderErrors = (): JSX.Element[] | JSX.Element => {
    if (documentError) {
      // Array of Errors
      if (Array.isArray(documentError)) {
        return documentError.map((error: string) => (<p key={error}>{error}</p>));
      }

      return (
        <p>{documentError}</p>
      );
    }

    return null;
  };

  // Render the Document to the Canvas after Initial Load, Page Change or Rotation
  React.useEffect(() => {
    if (imageDocument.location || imageDocument.file) {
      // Render based on the Document Type
      switch (imageDocument.contentType) {
        case FileType.Pdf:
          pdfToCancas();
          break;

        case FileType.Png:
        case FileType.Gif:
        case FileType.Jpg:
        case FileType.WebP:
        case FileType.Bmp:
          imageToCancas();
          break;

        default:
          Logging.Error('Unsupported Document Type:', imageDocument.contentType);
          setDocumentError(['Unsupported Document Type:', imageDocument.contentType]);
          setIsLoaded(true);
          break;
      }
    } else {
      if (imageDocument.contentType === FileType.Pdf) {
        setDocumentError(['Missing Url for the Document.']);
      } else {
        setDocumentError(['Missing Url for the Image.']);
      }
      setIsLoaded(true);
    }

    // This Function will run once the component unmounts (componentWillUnmount)
    return (): void => {
      Logging.Debug('removeEventListener: resize');

      // Get the Document Container
      const documentContainer: HTMLDivElement = document.getElementById(getId('sbsDocumentViewer')) as HTMLDivElement;
      const controlsContainer: HTMLDivElement = document.getElementById(getId('CanvasControls')) as HTMLDivElement;

      // Track the Mousement of the Mouse over the Body to Show/Hide the Controls
      if (documentContainer && controlsContainer) {
        document.body.removeEventListener(
          'mousemove',
          (event: MouseEvent) => onMouseMove(event, documentContainer, controlsContainer),
          false,
        );
      }
    };
  }, [pageNumber, isLoaded, rotateAngle]);

  // Document has an Error.  Show it
  if (documentError) {
    return (
      <div className="sbsDocumentViewerBlank">
        {renderErrors()}
      </div>
    );
  }

  // Document is still Loading.  Show loading Indicator
  if (!isLoaded) {
    return (
      <div className="sbsDocumentViewerBlank">
        <img src="/images/loading-animation.svg" alt="loading" className="sbsLoadingWaitingImage" />
        {
          (imageDocument.contentType === FileType.Pdf)
            ? (
              <div>Loading Document</div>
            )
            : (
              <div>Loading Image</div>
            )
        }
      </div>
    );
  }

  // Get the Style for Document Controls based on the Orientation
  const controlStyle: string = controlHorizontal
    ? `sbsDocumentViewerControls ${getId('sbsDocumentViewerControlsHorizontal')} ${additionalControlsStyle}`
    : `sbsDocumentViewerControls ${getId('sbsDocumentViewerControlsVertical')} ${additionalControlsStyle}`;

  Logging.Info('Document Viewer', imageDocument);

  // Show the Document and Controls
  return (
    <div id={getId('sbsDocumentViewer')} className="sbsDocumentViewer">
      <canvas id={getId('Canvas')} className="sbsDocumentViewerCanvas" />
      <div id={getId('CanvasControls')} className={controlStyle} onDoubleClick={onControlOrientation}>
        {
          numberPages > 1 && (
            <>
              {/* eslint-disable-next-line react/button-has-type */}
              <button id={getId('previousPageButton')} disabled={pageNumber === 1} onClick={onPreviousPage}>
                <img src="images/up-arrow-rounded.svg" alt="Previous Page" />
              </button>
              <p>{pageNumber}/{numberPages}</p>
              {/* eslint-disable-next-line react/button-has-type */}
              <button id={getId('nextPageButton')} disabled={pageNumber >= numberPages} onClick={onNextPage}>
                <img src="images/down-arrow-rounded.svg" alt="Next Page" />
              </button>
              <div className="sbsDocumentViewerControlSpacer" />
            </>
          )
        }
        {/* eslint-disable-next-line react/button-has-type */}
        <button id={getId('zoomInButton')}>
          <img src="images/plus-rounded.svg" alt="Zoom In" />
        </button>
        <p id={getId('zoomPercentage')}>100 %</p>
        {/* eslint-disable-next-line react/button-has-type */}
        <button id={getId('zoomOutButton')}>
          <img src="images/minus-rounded.svg" alt="Zoom Out" />
        </button>
        {
          canRotate && (
            <>
              <div className="sbsDocumentViewerControlSpacer" />
              {/* eslint-disable-next-line react/button-has-type */}
              <button id={getId('rotateButton')} onClick={onRotate}>
                <img src="images/rotate.svg" alt="Rotate" />
              </button>
            </>
          )
        }
        <div className="sbsDocumentViewerControlSpacer" />
        {/* eslint-disable-next-line react/button-has-type */}
        <button id={getId('restoreButton')}>
          <img src="images/maximize.svg" alt="Restore" />
        </button>
        {
          canDelete && onDelete && (
            <>
              <div className="sbsDocumentViewerControlSpacer" />
              {/* eslint-disable-next-line react/button-has-type */}
              <button id={getId('deleteButton')} onClick={(event) => onDelete(imageDocument)} className="deleteButton">
                <img src="images/delete.svg" alt="Delete" />
              </button>
            </>
          )
        }
      </div>
    </div>
  );
};

export default React.memo(DocumentViewer);
